import React, { useEffect, useState } from 'react';
import { Button, Modal } from '../common';
import { conversionsApi } from '../../api/conversions';
import { ConversionReport, ReportReason } from '../../types';

interface ReportConversionModalProps {
  isOpen: boolean;
  conversionId: string | null;
  conversionTitle?: string | null;
  onClose: () => void;
  /** Called with the created report after a successful submission. */
  onReported?: (report: ConversionReport) => void;
}

export const REPORT_REASON_OPTIONS: { value: ReportReason; label: string }[] = [
  { value: 'wrong_content', label: 'Wrong content was read (menus, cookie notice, etc.)' },
  { value: 'poor_audio', label: 'Poor audio quality (glitches, mispronunciation)' },
  { value: 'truncated', label: 'Audio stops early or content is missing' },
  { value: 'other', label: 'Something else' },
];

const DETAILS_MAX = 2000;

export default function ReportConversionModal({
  isOpen,
  conversionId,
  conversionTitle,
  onClose,
  onReported,
}: ReportConversionModalProps) {
  const [reason, setReason] = useState<ReportReason>('wrong_content');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setReason('wrong_content');
    setDetails('');
    setError(null);
    setSubmitting(false);
  }, [isOpen, conversionId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conversionId || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const report = await conversionsApi.reportConversion(conversionId, reason, details);
      onReported?.(report);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Report a problem" maxWidth="md">
      <form onSubmit={handleSubmit} className="space-y-5">
        <p className="font-body text-sm text-muted-foreground">
          Tell us what went wrong with{' '}
          <span className="text-foreground">{conversionTitle || 'this conversion'}</span>. An
          admin will listen to it, and may refund your credit.
        </p>

        <fieldset className="space-y-2">
          <legend className="block font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground mb-2">
            What happened?
          </legend>
          {REPORT_REASON_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex items-start gap-3 font-body text-sm text-foreground cursor-pointer"
            >
              <input
                type="radio"
                name="report-reason"
                value={option.value}
                checked={reason === option.value}
                onChange={() => setReason(option.value)}
                disabled={submitting}
                className="mt-1 accent-primary"
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>

        <div>
          <label
            htmlFor="report-details"
            className="block font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground mb-2"
          >
            Details (optional)
          </label>
          <textarea
            id="report-details"
            value={details}
            onChange={(e) => setDetails(e.target.value.slice(0, DETAILS_MAX))}
            placeholder="Anything that helps us find the problem, e.g. where in the audio it goes wrong."
            rows={4}
            maxLength={DETAILS_MAX}
            disabled={submitting}
            className="w-full font-body text-sm bg-background border border-border rounded-sm px-3 py-2 resize-y focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:opacity-60"
          />
          <p className="mt-1 font-mono text-xs tracking-wide text-muted-foreground text-right">
            {details.length}/{DETAILS_MAX}
          </p>
        </div>

        {error && (
          <p role="alert" className="font-body text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={submitting}>
            Send report
          </Button>
        </div>
      </form>
    </Modal>
  );
}
